/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';

import { BlIcon } from '@blend/shared-react';
import { mixins, colors as consumerColors, colors, ActionButton, LoadingSpinner } from '@blend/consumer-components';
import { mediaBreakpoints } from '../../../../common/responsive';
import { PreflightTestStatus } from './NetworkConnection';
import { DeviceCompatibilityStatus } from './DeviceCompatibilityStatus';

export namespace NetworkResult {
  export interface Props {
    preflightTestStatus: PreflightTestStatus;
    audioEnabled: boolean;
    videoEnabled: boolean;
    retriggerTests: () => void;
    isVideoConnectionBroken?: boolean;
    showDeviceCompatibilityStatus?: boolean;
  }
}

const styles = StyleSheet.create({
  iconWrapper: {
    color: consumerColors.mint,
    ...mixins.margin(0, 0, 0, 6),
  },
  unstableIconWrapper: {
    color: consumerColors.brickMinus4,
  },
  lastCheckedLabel: {
    fontSize: 16,
  },
  loadingLabel: {
    marginRight: 8,
  },
  checkedTime: {
    color: colors.catalystLight60,
    fontSize: 12,
    marginBottom: 0,
  },
  buttonWrapper: mixins.margin(24, 0, 0),
  button: {
    height: 50,
    [mediaBreakpoints.mobile]: {
      fontSize: 16,
    },
  },
  // since the loading spinner has height/width set to 50px while its actual height/width is 25 px, there's some whitespace around the spinner
  // by forcing its parent to this height/width, we remove the whitespace around the spinner
  loadingSpinnerWrapper: {
    height: 25,
    width: 25,
  },
  loadingWrapper: {
    display: 'flex',
  },
});

export const NetworkResult: React.FC<NetworkResult.Props> = ({
  preflightTestStatus,
  audioEnabled,
  videoEnabled,
  retriggerTests,
  isVideoConnectionBroken,
  showDeviceCompatibilityStatus,
}) => {
  const [checkedTime, setCheckedTime] = useState<string>(new Date().toLocaleString());
  const testNetwork = useCallback(() => {
    retriggerTests();
    setCheckedTime(new Date().toLocaleString());
  }, []);
  const testSuccess = preflightTestStatus === PreflightTestStatus.SUCCESS;
  const testComplete = preflightTestStatus !== PreflightTestStatus.IN_PROGRESS;
  const label = 'Connectivity with Audio Video servers';
  const icon = testSuccess ? 'success' : 'error';
  const statusLabel = testSuccess ? 'Good' : 'Weak';

  const cameraEnabled = videoEnabled ? 'Yes' : 'No';
  const microphoneEnabled = audioEnabled ? 'Yes' : 'No';
  const cameraEnabledIcon = videoEnabled ? 'success' : 'error';
  const microphoneEnabledIcon = audioEnabled ? 'success' : 'error';

  return (
    <>
      <p data-it="CameraEnabled">
        Camera Enabled: <strong>{cameraEnabled}</strong>
        <span className={css(styles.iconWrapper, !videoEnabled && styles.unstableIconWrapper)}>
          <BlIcon icon={cameraEnabledIcon} />
        </span>
      </p>
      <p data-it="MicrophoneEnabled">
        Microphone Enabled: <strong>{microphoneEnabled}</strong>
        <span className={css(styles.iconWrapper, !audioEnabled && styles.unstableIconWrapper)}>
          <BlIcon icon={microphoneEnabledIcon} />
        </span>
      </p>

      {preflightTestStatus === PreflightTestStatus.IN_PROGRESS ? (
        <div className={css(styles.loadingWrapper)} key={`${label}-check`}>
          <p className={css(styles.lastCheckedLabel, styles.loadingLabel)}>
            {label}: <strong>Loading</strong>
          </p>
          <div className={css(styles.loadingSpinnerWrapper)}>
            <LoadingSpinner size={'sm'} color={colors.catalystLight80} />
          </div>
        </div>
      ) : (
        <p key={`${label}-check`} className={css(styles.lastCheckedLabel)}>
          {label}: <strong>{isVideoConnectionBroken ? 'Error' : statusLabel}</strong>
          <span
            className={css(
              styles.iconWrapper,
              preflightTestStatus === PreflightTestStatus.FAILURE && styles.unstableIconWrapper,
            )}
          >
            <BlIcon icon={icon} />
          </span>
        </p>
      )}

      <p className={css(styles.checkedTime)}>{checkedTime}</p>
      <div className={css(styles.buttonWrapper)}>
        <ActionButton
          data-dd-privacy="allow"
          id="it-device-tests-retrigger-btn"
          customStyle={styles.button}
          option="secondary"
          sizing="md"
          onClick={testNetwork}
          color={colors.catalyst}
          font="UntitledSans"
          disabled={!testComplete}
        >
          Check again
        </ActionButton>
      </div>
      {showDeviceCompatibilityStatus && (
        <DeviceCompatibilityStatus
          preflightTestStatus={preflightTestStatus}
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
        />
      )}
    </>
  );
};
